import appManager from '@app/appManager';

const TRACE_ID_HEADER = 'X-Trace-Id';
const REQUEST_DURATION_HEADER = 'X-Request-Duration-Ms';

/**
 * Performs a fetch request and logs the request and response details.
 *
 * @param input - The resource that you wish to fetch. This can either be a
 * string containing the URL of the resource, or a `Request` object.
 * @param init - An optional object containing any custom settings that you
 * want to apply to the request.
 * @returns A `Promise` that resolves to a `Response` object, with an
 * overridden `text` method that logs errors if present.
 *
 * The function logs the following details:
 * - The operation name from the request body.
 * - The frontend request duration (total time from FE perspective).
 * - The backend processing duration (from X-Request-Duration-Ms header).
 * - The network latency (FE duration - BE duration).
 * - The trace ID from the response headers.
 * - The variables from the request body.
 * - Any errors present in the response.
 */
async function loggingFetch(
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<Response> {
  let body: any = {};
  let operationName = '';

  if (init?.body && typeof init.body === 'string') {
    body = JSON.parse(init.body);
    operationName = body.operationName;
  } else {
    operationName = 'FileUpload';
  }

  const start = Date.now();
  const response = await fetch(input, init);

  // Calculate latency metrics
  const frontendRequestDuration = Date.now() - start;
  const backendDurationMs = response.headers.get(REQUEST_DURATION_HEADER);
  const backendDuration = backendDurationMs
    ? parseInt(backendDurationMs, 10)
    : undefined;
  const networkLatency =
    backendDuration !== undefined
      ? frontendRequestDuration - backendDuration
      : undefined;

  appManager
    .logger('api')
    .info(`request for '${operationName}' in ${frontendRequestDuration}ms`, {
      traceId: response.headers.get(TRACE_ID_HEADER),
      variables: body.variables,
      frontendRequestDurationMs: frontendRequestDuration,
      backendProcessingDurationMs: backendDuration,
      networkLatencyMs: networkLatency,
    });

  return {
    ...response,

    async text() {
      const resultText = await response.text();
      const result = JSON.parse(resultText);
      if (result.errors) {
        appManager
          .logger('api')
          .error(`request for '${operationName}' failed`, {
            traceId: response.headers.get(TRACE_ID_HEADER),
            variables: body.variables,
            errors: result.errors,
          });
      }
      return resultText;
    },
  };
}

export default loggingFetch;
